/**
 * Pre-Screening Questions (P1-P4) + Q5 Struggles
 * P1-P4 are asked first, then Q5 (multi-select) determines profile + severity.
 * Q5 replaces the old Profile Selection and Assessment screens.
 *
 * Source: AidFone_FR_Translation_Johnley_March2026.docx
 */

export type AgeGroup = '50-64' | '65-74' | '75-84' | '85+';
export type LivingSituation =
  | 'home_alone'
  | 'home_family'
  | 'rpa_1_2'
  | 'rpa_3_4'
  | 'chsld';
export type HearingLevel = 'excellent' | 'good' | 'poor_ring' | 'poor';
export type VisionLevel = 'good' | 'some_difficulty' | 'significant';

export interface PreScreeningAnswers {
  ageGroup: AgeGroup;
  livingSituation: LivingSituation;
  hearing: HearingLevel;
  vision: VisionLevel;
}

export interface PreScreeningOption<T> {
  value: T;
  label: string;
  labelFr?: string;
  description?: string;
  descriptionFr?: string;
  impact?: string;
}

export interface PreScreeningQuestion<T> {
  id: string;
  code: string;
  text: string;
  textFr?: string;
  options: PreScreeningOption<T>[];
}

// P1: Age Group
export const AGE_GROUP_QUESTION: PreScreeningQuestion<AgeGroup> = {
  id: 'ageGroup',
  code: 'P1',
  text: 'How old is your loved one?',
  textFr: 'Quel \u00e2ge a votre proche?',
  options: [
    { value: '50-64', label: '50\u201364 years', labelFr: '50\u201364 ans' },
    { value: '65-74', label: '65\u201374 years', labelFr: '65\u201374 ans' },
    { value: '75-84', label: '75\u201384 years', labelFr: '75\u201384 ans' },
    { value: '85+', label: '85 years and older', labelFr: '85 ans et plus' },
  ],
};

// P2: Living Situation (apartment option removed per FR translation doc)
export const LIVING_SITUATION_QUESTION: PreScreeningQuestion<LivingSituation> = {
  id: 'livingSituation',
  code: 'P2',
  text: 'Where does your loved one live?',
  textFr: 'O\u00f9 habite votre proche?',
  options: [
    { value: 'home_alone', label: 'Alone at home', labelFr: 'Seul(e) \u00e0 la maison' },
    { value: 'home_family', label: 'At home with family', labelFr: '\u00c0 la maison avec la famille' },
    { value: 'rpa_1_2', label: 'Independent senior residence (RPA 1-2)', labelFr: 'R\u00e9sidence priv\u00e9e (cat\u00e9gorie 1\u20132)' },
    { value: 'rpa_3_4', label: 'Assisted senior residence (RPA 3-4)', labelFr: 'R\u00e9sidence priv\u00e9e (cat\u00e9gorie 3\u20134)' },
    { value: 'chsld', label: 'Long-term care (CHSLD)', labelFr: 'CHSLD' },
  ],
};

// P3: Hearing (no category labels shown — description text only)
export const HEARING_QUESTION: PreScreeningQuestion<HearingLevel> = {
  id: 'hearing',
  code: 'P3',
  text: "How is your loved one's hearing?",
  textFr: "Comment est l\u2019audition de votre proche?",
  options: [
    { value: 'excellent', label: 'Hears very well without aids', labelFr: 'Entend tr\u00e8s bien sans aide' },
    { value: 'good', label: 'Hears well with or without hearing aids', labelFr: 'Entend bien avec ou sans aide auditive' },
    { value: 'poor_ring', label: "Has difficulty \u2014 doesn't always hear the phone ring", labelFr: "A de la difficult\u00e9 \u2014 n\u2019entend pas toujours la sonnerie" },
    { value: 'poor', label: 'Very hard of hearing \u2014 needs visual or vibration alerts', labelFr: 'Tr\u00e8s malentendant(e) \u2014 a besoin d\u2019alertes visuelles ou vibratoires' },
  ],
};

// P4: Vision
export const VISION_QUESTION: PreScreeningQuestion<VisionLevel> = {
  id: 'vision',
  code: 'P4',
  text: "How is your loved one's vision?",
  textFr: "Comment est la vision de votre proche?",
  options: [
    { value: 'good', label: 'Sees well with or without glasses', labelFr: 'Voit bien avec ou sans lunettes' },
    { value: 'some_difficulty', label: 'Has difficulty reading a phone screen', labelFr: 'A de la difficult\u00e9 \u00e0 lire un \u00e9cran de t\u00e9l\u00e9phone' },
    { value: 'significant', label: 'Has significant vision loss', labelFr: 'A une perte de vision importante' },
  ],
};

// All pre-screening questions in order (P1-P4)
export const PRE_SCREENING_QUESTIONS = [
  AGE_GROUP_QUESTION,
  LIVING_SITUATION_QUESTION,
  HEARING_QUESTION,
  VISION_QUESTION,
] as const;

// Default answers
export const DEFAULT_PRE_SCREENING_ANSWERS: PreScreeningAnswers = {
  ageGroup: '65-74',
  livingSituation: 'home_family',
  hearing: 'good',
  vision: 'good',
};

// ============================================================================
// Q5 — Struggles (Multi-Select)
// Replaces Profile Selection + 4-question Assessment
// ============================================================================

export type Struggle =
  | 'memory_loss'
  | 'confusion'
  | 'wandering'
  | 'tremors'
  | 'arthritis'
  | 'limited_mobility'
  | 'low_vision'
  | 'difficulty_reading'
  | 'anxiety'
  | 'difficulty_dialing';

export interface StruggleOption {
  value: Struggle;
  label: string;
  labelFr: string;
  icon: string;
  profileMapping: 'cognitive' | 'physical' | 'visual';
  severityWeight: number; // higher = more severe
}

export const STRUGGLE_OPTIONS: StruggleOption[] = [
  { value: 'memory_loss', label: 'Forgetting names or numbers', labelFr: 'Oublie des noms ou des num\u00e9ros', icon: 'psychology_alt', profileMapping: 'cognitive', severityWeight: 2 },
  { value: 'confusion', label: 'Confusion or disorientation', labelFr: 'Confusion ou d\u00e9sorientation', icon: 'help_center', profileMapping: 'cognitive', severityWeight: 3 },
  { value: 'wandering', label: 'Wandering or getting lost', labelFr: '\u00c9garement ou se perd facilement', icon: 'explore_off', profileMapping: 'cognitive', severityWeight: 3 },
  { value: 'tremors', label: 'Tremors or shaking hands', labelFr: 'Tremblements ou mains qui tremblent', icon: 'pan_tool', profileMapping: 'physical', severityWeight: 2 },
  { value: 'arthritis', label: 'Arthritis or joint pain', labelFr: 'Arthrite ou douleurs articulaires', icon: 'accessibility_new', profileMapping: 'physical', severityWeight: 1 },
  { value: 'limited_mobility', label: 'Limited mobility or dexterity', labelFr: 'Mobilit\u00e9 ou dext\u00e9rit\u00e9 r\u00e9duite', icon: 'accessible', profileMapping: 'physical', severityWeight: 2 },
  { value: 'low_vision', label: 'Low vision or blindness', labelFr: 'Basse vision ou c\u00e9cit\u00e9', icon: 'visibility_off', profileMapping: 'visual', severityWeight: 3 },
  { value: 'difficulty_reading', label: 'Difficulty reading a screen', labelFr: 'Difficult\u00e9 \u00e0 lire un \u00e9cran', icon: 'text_fields', profileMapping: 'visual', severityWeight: 2 },
  { value: 'anxiety', label: 'Anxiety or fear of technology', labelFr: 'Anxi\u00e9t\u00e9 ou peur de la technologie', icon: 'mood_bad', profileMapping: 'cognitive', severityWeight: 1 },
  { value: 'difficulty_dialing', label: 'Difficulty dialing numbers', labelFr: 'Difficult\u00e9 \u00e0 composer un num\u00e9ro', icon: 'dialpad', profileMapping: 'physical', severityWeight: 1 },
];

export const STRUGGLES_QUESTION = {
  code: 'Q5',
  text: 'What does your loved one struggle with?',
  textFr: 'Avec quoi votre proche a-t-il de la difficult\u00e9?',
  subtitle: 'Select all that apply. This helps us choose the right phone layout.',
  subtitleFr: 'S\u00e9lectionnez tout ce qui s\u2019applique. Cela nous aide \u00e0 choisir le bon mod\u00e8le.',
};

/**
 * Determine profile type and severity from selected struggles.
 * Priority: cognitive > visual > physical (if tied)
 */
export function determineProfileFromStruggles(
  struggles: Struggle[]
): { profile: 'cognitive' | 'physical' | 'visual'; severity: 'mild' | 'moderate' | 'severe' } {
  if (struggles.length === 0) {
    return { profile: 'physical', severity: 'mild' };
  }

  const scores: Record<string, number> = { cognitive: 0, physical: 0, visual: 0 };
  let totalWeight = 0;

  for (const s of struggles) {
    const option = STRUGGLE_OPTIONS.find(o => o.value === s);
    if (option) {
      scores[option.profileMapping] += option.severityWeight;
      totalWeight += option.severityWeight;
    }
  }

  // Determine dominant profile
  let profile: 'cognitive' | 'physical' | 'visual' = 'physical';
  let maxScore = 0;
  // Priority order: cognitive > visual > physical
  for (const p of ['cognitive', 'visual', 'physical'] as const) {
    if (scores[p] > maxScore) {
      maxScore = scores[p];
      profile = p;
    }
  }

  // Determine severity based on count and weight
  let severity: 'mild' | 'moderate' | 'severe';
  if (totalWeight >= 7 || struggles.length >= 5) {
    severity = 'severe';
  } else if (totalWeight >= 4 || struggles.length >= 3) {
    severity = 'moderate';
  } else {
    severity = 'mild';
  }

  return { profile, severity };
}

// Legacy re-exports for backward compatibility
export type Gender = 'male' | 'female' | 'unspecified';
